import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useListLearningModulesQuery } from 'src/redux/slices/api/su';
import { useClientId, useSelLocale } from 'src/contexts/ClientContext';
import { LearningModuleQueryType, LearningModuleApiData } from 'src/types/api/learningModule';
import { InstanceLanguage } from 'src/constants/languageSelector';
import { InteractiveContentCard, SuCarousel } from '@amzn/seller-university-ui-components';
import { KatSpinner } from '@amzn/katal-react';
import { useBundle } from '@amzn/react-arb-tools';
import { t } from 'src/utils/translation';
import { BUNDLE_NAME, LEARNING_PATHWAY_STRING_ID } from 'src/constants/localization';
import { timeConverter } from 'src/utils/timeFormat';
import ErrorView from 'src/components/ErrorView';
import { ROUTE_SEARCH_PARAM } from 'src/constants/route';
import { FeatureName } from 'src/metrics/metricsFields';
import useUserMetrics from 'src/metrics/useUserMetrics';

export const LEARNING_MODULES_CAROUSEL_TEST_IDS = {
  LOADING_CONTAINER: 'learning-modules-carousel-loading',
} as const;

interface LearningModulesCarouselProps {
  subtopicId?: string;
  tagIds?: string[];
  className?: string;
  onEmpty?: () => void; // Callback when no modules are available
}

const LearningModulesCarousel: React.FC<LearningModulesCarouselProps> = ({
  subtopicId,
  tagIds,
  className,
  onEmpty,
}) => {
  const [learningPathwayBundle] = useBundle(BUNDLE_NAME.LEARNING_PATHWAY);
  const { clickMetric } = useUserMetrics();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = useClientId();
  const selLocale = useSelLocale();

  const hasValidInput = !!(subtopicId || tagIds?.length);

  const queryPattern = React.useMemo(() => {
    const conditions = [
      subtopicId && {
        queryType: LearningModuleQueryType.SubTopic,
        subtopicQuery: { subtopicId },
      },
      tagIds && {
        queryType: LearningModuleQueryType.Tags,
        tagsQuery: { tagIds },
      },
    ];

    return conditions.find(Boolean) || null;
  }, [subtopicId, tagIds]);

  const { data: modulesData, isLoading, isError } = useListLearningModulesQuery({
    clientId,
    selLocale,
    queryPattern: queryPattern!,
  }, { skip: !hasValidInput || !queryPattern });

  if (!hasValidInput) {
    return null;
  }

  const getDefaultInstance = (learningModule: LearningModuleApiData) => {
    return learningModule.instanceMap[learningModule.defaultLanguage as InstanceLanguage];
  };

  const getContentVariant = (learningModule: LearningModuleApiData) => {
    const defaultInstance = getDefaultInstance(learningModule);
    const instanceType = defaultInstance?.instanceType;
    if (instanceType?.toUpperCase() === 'VIDEO') {
      return 'Video' as const;
    }
    if (instanceType?.toUpperCase() === 'DOCUMENT') {
      return 'Pdf' as const;
    }
    return undefined;
  };

  const getModuleMetadata = (learningModule: LearningModuleApiData): string[] => {
    const defaultInstance = getDefaultInstance(learningModule);

    if (!defaultInstance) return [];

    if (defaultInstance.instanceType?.toUpperCase() === 'VIDEO' && defaultInstance.duration) {
      // For video, convert duration from seconds to minutes:seconds format
      return [timeConverter(defaultInstance.duration)];
    }
    if (defaultInstance.instanceType?.toUpperCase() === 'DOCUMENT' && defaultInstance.pageSize) {
      // For PDF, show page count
      return [t(
        learningPathwayBundle,
        LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_PDF_PAGES,
        {
          count: defaultInstance.pageSize,
        },
      )];
    }
    return [];
  };

  if (isLoading) {
    return (
      <div
        className="flex min-h-[15rem] items-center justify-center"
        data-testid={LEARNING_MODULES_CAROUSEL_TEST_IDS.LOADING_CONTAINER}
      >
        <KatSpinner />
      </div>
    );
  }

  // Handle errors
  if (isError) {
    return <ErrorView />;
  }

  // Handle empty results
  if (!modulesData?.modules.length) {
    if (queryPattern?.queryType === LearningModuleQueryType.Tags) {
      // Signal to parent that there are no modules so it can hide the title
      onEmpty?.();
      // Spike: Homepage Seller-to-Seller Module Carousel Implementation | Quip Doc
      // https://quip-amazon.com/NM3NAoQ5Pwb0/Spike-Homepage-Seller-to-Seller-Module-Carousel-Implementation
      return null; // Hide carousel completely for empty TAGS results
    }
    return <ErrorView />; // Show error view for SUB_TOPIC empty results
  }

  return (
    <SuCarousel
      items={modulesData.modules}
      getItemKey={(module: LearningModuleApiData) => module.moduleId}
      renderItem={(learningModule: LearningModuleApiData) => {
        const contentVariant = getContentVariant(learningModule);
        return (
          <InteractiveContentCard
            className="h-full"
            title={learningModule.title}
            previewDescription={learningModule.description}
            imageSrc={learningModule.thumbnailLink}
            {...(contentVariant && { contentVariant })}
            metadata={getModuleMetadata(learningModule)}
            layout="vertical"
            onClick={() => {
              clickMetric({
                feature: FeatureName.LearningModuleCard,
                metadata: {
                  url: window.location.href,
                  moduleId: learningModule.moduleId,
                  moduleTitle: learningModule.title,
                  parentId: subtopicId || `tags=${tagIds?.join(',')}`,
                },
              });

              const updatedSearchParams = new URLSearchParams(searchParams);
              updatedSearchParams.set(ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID, learningModule.moduleId);
              navigate({ search: updatedSearchParams.toString() }, { replace: true });
            }}
          />
        );
      }}
      layout={{
        xsmall: { cols: 1 },
        small: { cols: 2 },
        medium: { cols: 3 },
        large: { cols: 4 },
      }}
      className={className || '-mx-su-space-10'}
    />
  );
};

export default LearningModulesCarousel;
